// Live baseline — deterministic accessibility fixes applied on EVERY page,
// scanned or not, with no API and no AI. Before this, these fixes only ran when
// a prior scan had stored them, so unscanned pages got nothing.
//
// Every fix here is deliberately conservative: it only acts when the site has
// clearly NOT done the thing itself, and never restyles the site globally.
//   3.1.1 lang       — only when the script is unambiguous (never guesses Latin)
//   2.4.1 skip link  — only when the site has no skip link of its own
//   1.3.1 main       — only when there is no <main>/[role=main]
//   2.4.7 focus      — only on elements whose own focus outline was removed
// Live fixes are marked data-nagisot-live so reports can still show the source
// issue (patched at runtime ≠ fixed at source).

const LIVE = 'data-nagisot-live';
const MAX_RUNS = 8;
let runs = 0;
let timer: ReturnType<typeof setTimeout> | undefined;

// ── 3.1.1 language of page ────────────────────────────────────────────────────
function guessScriptLang(sample: string): string | null {
  const t = sample.slice(0, 3000);
  const count = (re: RegExp) => (t.match(re) || []).length;
  const scores: Array<[string, number]> = [
    ['he', count(/[֐-׿]/g)],
    ['ar', count(/[؀-ۿ]/g)],
    ['ru', count(/[Ѐ-ӿ]/g)],
    ['el', count(/[Ͱ-Ͽ]/g)],
    ['ja', count(/[぀-ヿ]/g)],
    ['ko', count(/[가-힯]/g)],
    ['zh', count(/[一-鿿]/g)],
  ];
  scores.sort((a, b) => b[1] - a[1]);
  const [lang, n] = scores[0]!;
  if (n < 20) return null; // not enough non-Latin text to be confident
  if (lang === 'zh' && count(/[぀-ヿ]/g) > 0) return 'ja'; // kana ⇒ Japanese
  return lang;
}

function ensureLang(): void {
  const html = document.documentElement;
  if (html.getAttribute('lang')?.trim()) return;
  const lang = guessScriptLang(document.body?.innerText || '');
  if (!lang) return; // Latin or mixed: leave it for the developer report
  html.setAttribute('lang', lang);
  if ((lang === 'he' || lang === 'ar') && !html.getAttribute('dir')) html.setAttribute('dir', 'rtl');
  html.setAttribute(LIVE, 'lang');
}

// ── 1.3.1 main landmark ───────────────────────────────────────────────────────
export function ensureMainLandmark(markLive = false): void {
  if (document.querySelector('main, [role=main]')) return;
  const cand = document.querySelector<HTMLElement>('#main, #content, .main, .content, [class*="main"]')
    || (Array.from(document.body.children)
      .filter((ch) => !['HEADER', 'NAV', 'FOOTER', 'ASIDE', 'SCRIPT', 'STYLE'].includes(ch.tagName)
        && !ch.hasAttribute('data-nagisot'))
      .sort((a, b) => (b.textContent?.length || 0) - (a.textContent?.length || 0))[0] as HTMLElement | undefined);
  if (!cand) return;
  cand.setAttribute('role', 'main');
  if (markLive) cand.setAttribute(LIVE, 'main');
}

// ── 2.4.1 skip to content ─────────────────────────────────────────────────────
const SKIP_TEXT: Record<string, string> = {
  he: 'דלג לתוכן הראשי',
  ar: 'تخطَّ إلى المحتوى الرئيسي',
  ru: 'Перейти к основному содержанию',
  fr: 'Aller au contenu principal',
  es: 'Saltar al contenido principal',
  de: 'Zum Hauptinhalt springen',
};

function siteHasOwnSkipLink(): boolean {
  const firstLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('body a[href^="#"]')).slice(0, 5);
  return firstLinks.some((a) => !a.classList.contains('nagisot-skip')
    && /skip|דלג|main|content|תוכן|محتوى|контент/i.test(`${a.textContent} ${a.getAttribute('href')}`));
}

function mainTarget(): HTMLElement | null {
  return document.querySelector<HTMLElement>('main, [role=main], #main, #content') || document.querySelector<HTMLElement>('h1');
}

export function ensureSkipLink(markLive = false): void {
  const existing = document.querySelector<HTMLAnchorElement>('a.nagisot-skip');
  const target = mainTarget();
  if (existing) {
    // A late-hydrating SPA may only now have rendered its main content.
    if (existing.getAttribute('href') === '#' && target) {
      if (!target.id) target.id = 'nagisot-main';
      existing.href = `#${target.id}`;
    }
    return;
  }
  if (markLive && siteHasOwnSkipLink()) return;
  let id = target?.id;
  if (target && !id) { id = 'nagisot-main'; target.id = id; }
  // ensureLang() runs first, so a Hebrew/Arabic page without lang already has it
  // here; anything still unlabeled is Latin/unknown → English, never Hebrew.
  const lang = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
  const a = document.createElement('a');
  a.className = 'nagisot-skip';
  a.href = id ? `#${id}` : '#';
  a.textContent = SKIP_TEXT[lang] || 'Skip to main content';
  // Hidden by translating UP, not sideways: `left:-9999px` overflows into the
  // scrollable area on RTL pages (measured: 1024px → 11023px of horizontal
  // scroll — itself a 1.4.10 reflow failure). Overflow above the top is never
  // scrollable in either direction.
  a.style.cssText = 'position:absolute;top:0;inset-inline-start:0;transform:translateY(-120%);z-index:2147483647;background:#0B1220;color:#fff;padding:8px 14px;font:600 14px/1.4 system-ui,sans-serif;text-decoration:none;';
  a.addEventListener('focus', () => { a.style.transform = 'none'; });
  a.addEventListener('blur', () => { a.style.transform = 'translateY(-120%)'; });
  if (markLive) a.setAttribute(LIVE, 'skip-link');
  document.body.insertBefore(a, document.body.firstChild);
}

// ── 2.4.7 focus visible ───────────────────────────────────────────────────────
function startFocusRepair(): void {
  if (document.getElementById('nagisot-focus-style')) return;
  const style = document.createElement('style');
  style.id = 'nagisot-focus-style';
  style.textContent = `[data-nagisot-focus]:focus-visible{outline:3px solid #fbbf24!important;outline-offset:2px!important;box-shadow:0 0 0 5px #0B1220!important}`;
  document.head.appendChild(style);
  document.addEventListener('focusin', (e) => {
    const el = e.target as HTMLElement | null;
    if (!el || el.hasAttribute('data-nagisot-focus') || el.hasAttribute('data-nagisot')) return;
    try { if (!el.matches(':focus-visible')) return; } catch { return; }
    const cs = getComputedStyle(el);
    const noOutline = cs.outlineStyle === 'none' || parseFloat(cs.outlineWidth) === 0;
    if (noOutline && cs.boxShadow === 'none') {
      el.setAttribute('data-nagisot-focus', '1'); // the :focus-visible rule applies immediately
    }
  }, true);
}

// ── runner ────────────────────────────────────────────────────────────────────
function sweep(): void {
  if (runs >= MAX_RUNS || !document.body) return;
  runs++;
  try { ensureLang(); } catch { /* noop */ }
  try { ensureMainLandmark(true); } catch { /* noop */ }
  try { ensureSkipLink(true); } catch { /* noop */ }
}

export function startLiveBaseline(): void {
  const boot = (): void => {
    sweep();
    startFocusRepair();
    window.addEventListener('load', () => { clearTimeout(timer); timer = setTimeout(sweep, 200); }, { once: true });
    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(() => { clearTimeout(timer); timer = setTimeout(sweep, 1000); })
        .observe(document.body, { childList: true, subtree: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
